import { Trans, useTranslation } from 'next-i18next'

import FaqAccordion, { ItemLink } from './faq-accordion'

const Renters = () => {
  const { t } = useTranslation()

  const RENTER_TIPS = [
    {
      title: t('faq:When should I pay my deposits?'),
      path: 'when-to-pay-deposits',
      answers: [
        {
          steps: [
            <Trans
              key="You should only give your security deposit, also know as a damage deposit"
              i18nKey="faq:You should only give your security deposit, also know as a damage deposit"
            >
              You should only give your{' '}
              <ItemLink
                href="https://liv.rent/blog/2018/12/deposit-debrief-your-questions-on-security-deposits-answered/"
                rel="noopener noreferrer"
                target="_blank"
              >
                security deposit
              </ItemLink>
              , also known as a damage deposit, at the time of signing or after
              a lease agreement has been signed. The deposit can be no more than
              half of the first month&apos;s rent and must be paid within 30
              days of entering into the tenancy agreement. Once you have paid
              your deposit, the tenancy is established. In other words, you
              can&apos;t move in somewhere else and your landlord can&apos;t
              rent it out to someone else.
            </Trans>
          ]
        },
        {
          steps: [
            <Trans
              key="Rental Guide: How Rent Works In Canada — A Provincial Comparison"
              i18nKey="faq:Rental Guide: How Rent Works In Canada — A Provincial Comparison"
            >
              Rental Guide:{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/12/how-does-rent-work-in-canada-a-provincial-comparison/"
                rel="noopener noreferrer"
                target="_blank"
              >
                How Rent Works In Canada — A Provincial Comparison
              </ItemLink>
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('faq:What to do if my landlord refuses to pay my deposit back?'),
      path: 'landlord-refuses-to-return-deposit',
      answers: [
        {
          steps: [
            <Trans
              key="If your landlord refuses to return your security deposit"
              i18nKey="faq:If your landlord refuses to return your security deposit"
            >
              If your landlord refuses to return your security deposit, then you
              can apply for{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/12/how-to-dispute-resolution/"
                rel="noopener noreferrer"
                target="_blank"
              >
                dispute resolution
              </ItemLink>{' '}
              requesting your deposit be returned.
            </Trans>
          ]
        },
        {
          steps: [
            <Trans
              key="However, before applying for dispute resolution, make sure you have complied with the following rules"
              i18nKey="faq:However, before applying for dispute resolution, make sure you have complied with the following rules"
            >
              However, before applying for dispute resolution, make sure you
              have complied with the following rules:
              <ol>
                <li>
                  Provided a forwarding address to your landlord (in writing).
                </li>
                <li>
                  Thoroughly cleaned your unit and removed all belongings.
                </li>
                <li>
                  Participated in the move-out inspection and completed the
                  move-out condition inspection report.
                </li>
              </ol>
            </Trans>
          ]
        },
        {
          steps: [
            t(
              'faq:Once your tenancy has ended and you have complied with all the rules, your landlord must return your deposit within 15 days of moving out. An exception to this is when your landlord requests compensation for damage or applies for dispute resolution to request keeping part of the deposit.'
            )
          ]
        },
        {
          steps: [
            t('faq:Click here for more info >> '),
            <ItemLink
              key="Ultimate Guide on Security Deposits"
              href="https://liv.rent/blog/2018/12/deposit-debrief-your-questions-on-security-deposits-answered/"
              rel="noopener noreferrer"
              target="_blank"
            >
              {t('faq:Ultimate Guide on Security Deposits')}
            </ItemLink>
          ]
        }
      ]
    },
    {
      title: t('faq:Can landlords refuse pets?'),
      path: 'can-landlords-refuse-pets',
      answers: [
        {
          steps: [
            t(
              'faq:Depending on where you are renting, landlords may or may not have the right to refuse pets. In Ontario, landlords cannot refuse a tenant who has a pet. The only exception is if the rental property is a condominium.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:However, for most provinces like Alberta, Manitoba, Quebec, and B.C. — landlords can refuse pets. The only exception is a tenant with a guide or service animal and landlords cannot request a pet damage deposit in this case.'
            )
          ]
        },
        {
          steps: [
            <Trans
              key="Additionally, if pets are allowed, landlords have the right to restrict the kind or breed, the size and number of pets."
              i18nKey="faq:Additionally, if pets are allowed, landlords have the right to restrict the kind or breed, the size and number of pets."
            >
              Additionally, if pets are allowed, landlords have the right to
              restrict the kind or breed, the size and number of pets. Keep in
              mind that it is quite common for landlords to allow small pets,
              but not large ones. Learn more at{' '}
              <ItemLink
                href="https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/during-a-tenancy/pets"
                rel="noopener noreferrer"
                target="_blank"
              >
                gov.bc.ca
              </ItemLink>
            </Trans>
          ]
        },
        {
          steps: [
            t('faq:Search on liv.rent to find pet friendly rentals in '),
            {
              text: t('Vancouver'),
              href: 'https://liv.rent/rental-listings/city/vancouver?pet_policy=ALLOWED'
            },
            ', ',
            {
              text: t('Burnaby'),
              href: 'https://liv.rent/rental-listings/city/burnaby?pet_policy=ALLOWED'
            },
            ', ',
            {
              text: t('Richmond'),
              href: 'https://liv.rent/rental-listings/city/richmond?pet_policy=ALLOWED'
            },
            ', ',
            {
              text: t('Surrey'),
              href: 'https://liv.rent/rental-listings/city/surrey?pet_policy=ALLOWED'
            },
            t('faq: and more. Simply apply the "Pet Policy" filter. ')
          ]
        }
      ]
    },
    {
      title: t('faq:Do I need to include my roommate on my lease agreement?'),
      path: 'include-roommate-rental-agreement',
      answers: [
        {
          steps: [
            t(
              'faq:Three types of roommate setups',
              'Depending on which roommate setup you have, the person living with you may or may not need to be on the lease agreement. There are three types of setups for '
            ),
            {
              text: t(
                'faq:roommates and occupants',
                'roommates and occupants:'
              ),
              href: 'https://tenants.bc.ca/your-tenancy/roommates/'
            }
          ]
        },
        {
          steps: [
            t(
              'faq:1) Co-tenants are roommates who share a single tenancy agreement. In this case, the name of the co-tenant must be on the lease agreement. Co-tenants are jointly responsible for everything related to the tenancy. If the landlord issues an eviction notice because the full rent was not paid on time, then this will apply to everyone.'
            )
          ]
        },
        {
          steps: [
            t(
              "faq:2) Tenants in common have separate tenancy agreements with the landlord. (Landlord rents out individual rooms in a house under separate agreements.) In this case, the person living with you must be on the lease agreement but on one that is separate from yourself. If anything goes wrong, you won't face any legal implications."
            )
          ]
        },
        {
          steps: [
            t(
              'faq:3) Roommates/ occupants do not rent from the landlord. Instead, they rent from the tenant. In this case, the person living with you does not have to be on the lease. However, in this kind of arrangement, the roommate/occupant will not be protected by the '
            ),
            {
              text: t('faq:Residential Tenancy Act'),
              href: 'https://liv.rent/blog/2019/09/residential-tenancy-agreement-explained/'
            },
            t('faq:. Also, for any disputes, they cannot use the '),
            {
              text: t("faq:Residential Tenancy Branch (RTB)'s"),
              href: 'https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies'
            },
            t(
              'faq: dispute resolution system. They can only settle through Small Claims Court, Civil Resolution Tribunal or the BC Supreme Court.'
            )
          ]
        },
        {
          steps: [
            <Trans
              key="It is highly recommended that you draft a roommate agreement"
              i18nKey="faq:It is highly recommended that you draft a roommate agreement"
            >
              It is highly recommended that you draft a{' '}
              <ItemLink
                href="https://liv.rent/blog/2020/06/finding-a-roommate-during-covid19/"
                rel="noopener noreferrer"
                target="_blank"
              >
                roommate agreement
              </ItemLink>{' '}
              to set expectations and ground rules. Learn more about finding a
              roommate and download the free{' '}
              <ItemLink
                href="https://liv.rent/blog/2020/06/finding-a-roommate-during-covid19/"
                rel="noopener noreferrer"
                target="_blank"
              >
                roommate agreement template
              </ItemLink>
              .
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('faq:What are considered emergency repairs on a rental unit?'),
      path: 'what-are-emergency-repairs',
      answers: [
        {
          steps: [
            t(
              "faq:When the tenant's health or safety is impacted and/or the property is put at risk, then it would be classified as an emergency repair. By law, landlords are responsible for handling and paying for all "
            ),
            {
              text: t('faq:emergency repairs'),
              href: 'https://www.cmhc-schl.gc.ca/en/rental-housing/i-am-renting/maintenance-repairs'
            }
          ]
        },
        {
          steps: [
            t('faq:The following situations would be considered '),
            {
              text: t('faq:emergency repairs'),
              href: 'https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/during-a-tenancy/repairs-and-maintenance'
            },
            ':',
            <ul key="Emergency repairs list">
              <li>{t('faq:Major leaks in pipes or the roof')}</li>
              <li>{t('faq:Broken primary heating system ')}</li>
              <li>{t('faq:Damaged plumbing system')}</li>
              <li>
                {t(
                  'faq:Malfunctioning electrical system that can create a risk of fire and/or electrocution'
                )}
              </li>
              <li>{t('faq:Defective locks that make the unit insecure')}</li>
            </ul>
          ]
        },
        {
          steps: [
            t(
              'faq:Your landlord is required to provide an emergency contact number either in writing or have it posted in a common area. If you encounter an urgent repair that meets the definition of an "emergency", you should try calling the emergency contact at least twice while leaving a reasonable amount of time between each attempt.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:If there is still no response, then you can arrange to have the repairs done at a reasonable cost. Afterwards, your landlord must reimburse you for the '
            ),
            {
              text: t('faq:emergency repair'),
              href: 'https://tenants.bc.ca/your-tenancy/repairs-and-maintenance/'
            },
            t(
              'faq:. In order to receive reimbursement, you must submit all receipts and a summary documenting your process. Make sure to keep copies of your research effort to show that you did your best in finding a fair price.'
            )
          ]
        },
        {
          steps: [
            t('faq:Check out the following '),
            {
              text: t('faq:blog post'),
              href: 'https://liv.rent/blog/2018/12/property-maintenance-someones-gotta-do-it/'
            },
            t(
              'faq: for a complete list of landlord and tenant responsibilities'
            )
          ]
        }
      ]
    },
    {
      title: t('faq:What information can a landlord ask for?'),
      path: 'what-information-can-landlords-ask',
      answers: [
        {
          steps: [
            t(
              'faq:Landlords can ask for the following information',
              'Landlords can ask for the following information:'
            ),
            <ul key="Landlord requested information list">
              <li>{t('faq:Credit Check')}</li>
              <li>{t('faq:References')}</li>
              <li>{t('faq:Salary')}</li>
              <li>{t('faq:Post-dated Cheques')}</li>
            </ul>
          ]
        },
        {
          steps: [
            t('faq:Protect yourself and '),
            {
              text: t('faq:learn more'),
              href: 'https://liv.rent/blog/2020/01/landlord-tenant-information-privacy/'
            },
            t('faq: about what information landlords can and cannot ask for')
          ]
        },
        {
          steps: [
            <Trans
              key="Prospective landlords may request a credit check to assess your ability to pay rent. To run a credit check, you must provide your name, address, and date of birth. Landlords may also ask for your driver's licence, passport, or social insurance number (SIN). Although this information is not needed, it will allow the landlord to obtain a more detailed report from the organization providing the credit check. Learn about"
              i18nKey="faq:Prospective landlords may request a credit check to assess your ability to pay rent. To run a credit check, you must provide your name, address, and date of birth. Landlords may also ask for your driver's licence, passport, or social insurance number (SIN). Although this information is not needed, it will allow the landlord to obtain a more detailed report from the organization providing the credit check. Learn about"
            >
              Prospective landlords may request a credit check to assess your
              ability to pay rent. To run a credit check, you must provide your
              name, address, and date of birth. Landlords may also ask for your
              driver&apos;s licence, passport, or social insurance number (SIN).
              Although this information is not needed, it will allow the
              landlord to obtain a more detailed report from the organization
              providing the credit check. Learn about{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/10/trust-score-explained/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Trust Score
              </ItemLink>
              , our intelligent rating system to establish your credibility as a
              renter.
            </Trans>
          ]
        },
        {
          steps: [
            <Trans
              key="NOTE: Landlords are required to comply with the"
              i18nKey="faq:NOTE: Landlords are required to comply with the"
            >
              NOTE: Landlords are required to comply with the{' '}
              <ItemLink
                href="https://www.priv.gc.ca/en/privacy-topics/privacy-laws-in-canada/the-personal-information-protection-and-electronic-documents-act-pipeda/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Personal Information Protection and Electronic Documents Act
                (PIPEDA)
              </ItemLink>
              . As a result, landlords must provide an explanation when they
              request certain documents. They are not allowed to collect more
              information than what&apos;s needed for the stated purpose.
            </Trans>
          ]
        }
      ]
    },
    {
      title: t("faq:Who's responsible for fixing stuff?"),
      path: 'who-responsible-for-repairs',
      answers: [
        {
          steps: [
            t(
              'faq:Tenants are responsible for repairing damages that are caused by someone living in (e.g. roommates) or visiting the unit (e.g. guests). This also applies to damages caused by pets. For all other rental repairs, landlords are responsible for fixing them. However, tenants are responsible for notifying the landlord if something needs to be repaired.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:Additionally, tenants are required to meet the minimum standard of health and cleanliness involving the unit and surrounding areas such as hallway, yard and laundry facility.'
            )
          ]
        },
        {
          steps: [
            t('faq:Useful resources', 'Here are some useful resources:'),
            <br key="breakline" />,
            t('faq:Complete list of '),
            {
              text: t('faq:landlord and tenant responsibilities'),
              href: 'https://liv.rent/blog/2018/12/property-maintenance-someones-gotta-do-it/'
            },
            <br key="breakline" />,
            {
              text: t('faq:- Rental repairs and maintenance responsibilities '),
              href: 'https://liv.rent/blog/2018/12/rental-repairs-who-is-responsible-for-what/'
            }
          ]
        }
      ]
    },
    {
      title: t('faq:Who is responsible for snow removal at a rental property?'),
      path: 'who-responsible-snow-removal',
      answers: [
        {
          steps: [
            <Trans
              key="In B.C. the responsibility of snow removal"
              i18nKey="faq:In B.C. the responsibility of snow removal"
            >
              In B.C. the responsibility of{' '}
              <ItemLink
                href="http://www2.gov.bc.ca/assets/gov/housing-and-tenancy/residential-tenancies/policy-guidelines/gl01.pdf"
                rel="noopener noreferrer"
                target="_blank"
              >
                snow removal
              </ItemLink>{' '}
              depends on the rental property type. For a multi-unit building,
              landlords are responsible for snow removal. On the other hand, for
              a single- or multi-family dwelling or townhouse, renters are
              responsible for shovelling snow.
            </Trans>
          ]
        },
        {
          steps: [
            <Trans
              key="In Ontario, landlords are responsible for snow and ice removal. Under section 26 of"
              i18nKey="faq:In Ontario, landlords are responsible for snow and ice removal. Under section 26 of"
            >
              In Ontario, landlords are responsible for snow and ice removal.
              Under section 26 of{' '}
              <ItemLink
                href="https://ontariolandlords.org/blog/tenants-do-not-have-to-shovel-snow-even-if-you-have-a-separate-agreement-to-do-so/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Ontario Regulation 517
              </ItemLink>
              , landlords are obligated to maintain exterior common areas which
              includes removing weeds and shovelling snow. Even if the
              residential lease has a clause that requires the tenant to remove
              snow and ice from a rental property, this is not enforceable
              because landlords cannot transfer this responsibility to the
              tenant.
            </Trans>
          ]
        },
        {
          steps: [
            <Trans
              key="Learn more by viewing our complete list of landlord and tenant responsibilities"
              i18nKey="faq:Learn more by viewing our complete list of landlord and tenant responsibilities"
            >
              Learn more by viewing our{' '}
              <ItemLink
                href="https://liv.rent/blog/2018/12/property-maintenance-someones-gotta-do-it/"
                rel="noopener noreferrer"
                target="_blank"
              >
                complete list of landlord and tenant responsibilities
              </ItemLink>
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('faq:What do I do when an appliance breaks down?'),
      path: 'what-to-do-broken-appliance',
      answers: [
        {
          steps: [
            t('faq:Landlords are responsible for '),
            {
              text: t('faq:appliances'),
              href: 'https://liv.rent/blog/2018/12/property-maintenance-someones-gotta-do-it/'
            },
            t(
              "faq: in a rental property (except for appliances that are not stated in the rental agreement).As a result, the basic maintenance of appliances, as well as replacement or repairs related to standard wear or tear, will fall under the landlord's domain. However, tenants are responsible for repair costs if they were the one to have caused the damage."
            )
          ]
        }
      ]
    },
    {
      title: t('faq:Can a landlord make a tenant pay for repairs?'),
      path: 'can-landlord-make-me-pay-repairs',
      answers: [
        {
          steps: [
            t(
              'faq:If the damage was not caused by the tenant, their pets or guests, then landlords cannot make a tenant pay for '
            ),
            {
              text: t('faq:repairs'),
              href: 'https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/during-a-tenancy/repairs-and-maintenance'
            },
            t(
              'faq:. Likewise, a tenant cannot make the repairs themselves and charge the landlord for the costs unless they received a written agreement from the landlord beforehand.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:For regular or routine repairs, tenants must request in writing - describing the problem and providing the landlord a reasonable amount of time to fix it. If the landlord refuses to fix the problem, then the tenant can apply for '
            ),
            {
              text: t('faq:dispute resolution'),
              href: 'https://liv.rent/blog/2019/12/how-to-dispute-resolution/'
            },
            t(
              'faq: to obtain an order requiring the landlord to make the repair and apply for monetary compensation.'
            )
          ]
        },
        {
          steps: [
            t('faq:For '),
            {
              text: t('faq:emergency repairs'),
              href: 'https://tenants.bc.ca/your-tenancy/repairs-and-maintenance/'
            },
            t(
              'faq:, tenants must reach out to the emergency contact at least twice while leaving a reasonable amount of time between each attempt. If there is still no response, then they can arrange to have the repairs done at a reasonable cost and request for reimbursement from the landlord. In order to receive payment for the repair, tenants must submit all receipts and a summary documenting the research process which shows their best effort in trying to find a fair price.'
            )
          ]
        }
      ]
    },
    {
      title: t('faq:What happens if I pay my rent late?'),
      path: 'what-happens-if-i-pay-rent-late',
      answers: [
        {
          steps: [
            t(
              'faq:Yes, landlords can evict you for paying rent late. In accordance with the Residential Tenancy Act, your landlord can give a '
            ),
            {
              text: t('faq:10 Day Notice to End Tenancy'),
              href: 'https://liv.rent/blog/2019/11/causes-for-eviction/'
            },
            t(
              'faq: if rent or utilities was not paid in full and by midnight on the due date.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:If you pay the outstanding amount within five days of receiving the '
            ),
            {
              text: t('faq:notice'),
              href: 'https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/during-a-tenancy/paying-rent'
            },
            t('faq:, then the notice is cancelled and your tenancy continues.')
          ]
        },
        {
          steps: [
            t(
              'faq:Rent not on time',
              'NOTE: Even if you did not pay the rent on time, landlords do not have the right to remove or limit access to any of your personal property unless they received written permission from the court or they followed the procedure for '
            ),
            {
              text: t('faq:abandoned property'),
              href: 'https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/ending-a-tenancy/items-left-behind#Abandonment'
            }
          ]
        }
      ]
    },
    {
      title: t('faq:Do I need to purchase tenant insurance?'),
      path: 'do-i-need-tenant-insurance',
      answers: [
        {
          steps: [
            t('faq:No, '),
            {
              text: t('faq:tenant insurance'),
              href: 'https://liv.rent/blog/2018/10/5-reasons-you-should-have-tenants-insurance/'
            },
            t(
              'faq: is not mandatory. However, landlords may request it as a condition in the lease.'
            )
          ]
        },
        {
          steps: [
            t('faq:Although you are not obligated to get '),
            {
              text: t('faq:tenant insurance'),
              href: 'https://liv.rent/blog/2018/10/5-reasons-you-should-have-tenants-insurance/'
            },
            t(
              "faq: (also known as renter's insurance), it is highly recommended because it can end up saving you a lot of money in the long run. For instance, if another tenant or neighbour caused an accident that resulted in subsequent damage to your personal property, you are responsible for covering the cost to repair or replace your items."
            )
          ]
        },
        {
          steps: [
            t(
              'faq:With tenant insurance, it can cover the damage and losses to your personal property. Read on to '
            ),
            {
              text: t('faq:learn more'),
              href: 'https://liv.rent/blog/2018/10/5-reasons-you-should-have-tenants-insurance/'
            },
            t('faq: about tenant insurance and its benefits')
          ]
        }
      ]
    },
    {
      title: t('faq:Can a landlord ask for another security deposit?'),
      path: 'can-landlord-ask-for-another-deposit',
      answers: [
        {
          steps: [
            t(
              'faq:No, landlords cannot ask for additional security deposit. In BC, landlords can only request one security deposit or pet damage '
            ),
            {
              text: t('faq:deposit'),
              href: 'https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/starting-a-tenancy/deposits-and-fees'
            },
            t('faq: per tenancy agreement')
          ]
        },
        {
          steps: [
            t('faq:The '),
            {
              text: t('faq:security deposit'),
              href: 'https://liv.rent/blog/2018/12/deposit-debrief-your-questions-on-security-deposits-answered/'
            },
            t(
              "faq: (or damage deposit) can be no more than half of the first month's rent, and is used to cover any damages to the rental unit. Likewise, a "
            ),
            {
              text: t('faq:pet damage deposit'),
              href: 'https://liv.rent/blog/2018/12/deposit-debrief-your-questions-on-security-deposits-answered/'
            },
            t(
              "faq: can be no more than half of the first month's rent regardless of the number of pets allowed."
            )
          ]
        },
        {
          steps: [
            {
              text: t('faq:Learn more'),
              href: 'https://liv.rent/blog/2018/12/deposit-debrief-your-questions-on-security-deposits-answered/'
            },
            t('faq: about security deposits in BC')
          ]
        },
        {
          steps: [
            t(
              'faq:In Ontario, instead of a security deposit, landlords can request for a rent deposit (also known as '
            ),
            {
              text: t("faq:Last Month's Rent"),
              href: 'https://stepstojustice.ca/questions/housing-law/do-i-have-pay-deposit-when-i-rent-new-place'
            },
            t(
              "faq:). On a yearly or monthly rental, the rent deposit can be no more than one month's rent. On a weekly rental, the rent deposit can be no more than one week's rent. Unlike a security deposit which would be returned at the end of a lease term (provided that there were no damages), the rent deposit would be used for the final period of rent. Landlords may not use the rent deposit for damages or other costs."
            )
          ]
        }
      ]
    },
    {
      title: t('faq:Can a landlord charge me a key deposit?'),
      path: 'can-landlord-charge-key-deposit',
      answers: [
        {
          steps: [
            {
              text: t('faq:Key deposit'),
              href: 'https://stepstojustice.ca/questions/housing-law/do-i-have-pay-deposit-when-i-rent-new-place'
            },
            t(
              'faq: is legal in Ontario. However, in other Canadian provinces, such a deposit is either not allowed or considered part of the security deposit.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:In Ontario, the key deposit can be no more than the actual cost to replace the keys and must be refundable — landlords must give back the deposit when their tenant returns the keys.'
            )
          ]
        },
        {
          steps: [
            t('faq:In BC, landlords cannot charge a fee for the '),
            {
              text: t("faq:tenant's primary key"),
              href: 'https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/starting-a-tenancy/deposits-and-fees'
            },
            t(
              'faq:, which serves as the only means for getting into the rental property. However, a refundable fee can be charged for additional keys or access devices, like a fob.'
            )
          ]
        }
      ]
    },
    {
      title: t('faq:Are landlords allowed to increase rent?'),
      path: 'can-landlord-increase-rent',
      answers: [
        {
          steps: [
            t(
              'faq:Yes, landlords are allowed to increase rent, but the requirements will vary by province.'
            )
          ]
        },
        {
          steps: [
            <Trans
              key="In BC, landlords must issue a Notice of Rent Increase"
              i18nKey="faq:In BC, landlords must issue a Notice of Rent Increase"
            >
              In BC, landlords must issue a{' '}
              <ItemLink
                href="https://liv.rent/blog/2020/08/2020-guide-to-bc-tenancy-forms/#rent-increase"
                rel="noopener noreferrer"
                target="_blank"
              >
                Notice of Rent Increase
              </ItemLink>{' '}
              with a minimum of three months in advance or the proposed increase
              taking effect. In addition, landlords can only increase rent once
              in a 12 month period and cannot exceed the maximum{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/09/bc-rent-increase/"
                rel="noopener noreferrer"
                target="_blank"
              >
                allowable rent amount
              </ItemLink>
              .
            </Trans>
          ]
        },
        {
          steps: [
            t(
              'faq:Similar to B.C., landlords in Ontario must also provide the tenant with a written notice to state their intention of raising rent three months in advance and are restricted to once every 12 month period.'
            )
          ]
        }
      ]
    },
    {
      title: t('faq:How do I end my lease early?'),
      path: 'how-to-end-lease-early',
      answers: [
        {
          steps: [
            t('faq:In order to get out of a '),
            {
              text: t('faq:lease early'),
              href: 'https://liv.rent/blog/2019/10/landlord-guide-end-extend-tenancy-agreement/'
            },
            t(
              'faq:, you must provide your landlord with a written notice of your intent to terminate the '
            ),
            {
              text: t('faq:fixed term tenancy agreement'),
              href: 'https://liv.rent/blog/2019/09/residential-tenancy-agreement-explained/'
            },
            t(
              'faq: earlier than the end date. Afterwards, both you and your landlord must sign the '
            ),
            {
              text: t('faq:Mutual Agreement to End Tenancy'),
              href: 'https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/ending-a-tenancy/tenant-notice'
            },
            t('faq: form and each keep a copy.')
          ]
        },
        {
          steps: [
            t(
              'faq:If you decide to end your lease early, be prepared to reimburse your landlord for costs associated with re-renting the unit (e.g. advertising or lost rent). As an alternative, you can also ask your landlord for written permission to help them find a new renter either through subletting or assigning the tenancy agreement to someone else.'
            )
          ]
        }
      ]
    },
    {
      title: t(
        'faq:Are landlords allowed to come into my rental property without notice?'
      ),
      path: 'can-landlord-enter-property-no-notice',
      answers: [
        {
          steps: [
            t(
              'faq:In order to enter the rental property, landlords must serve a '
            ),
            {
              text: t('faq:notice of entry'),
              href: 'https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies/during-a-tenancy/landlord-s-access'
            },
            t(
              'faq: at least 24 hours in advance, but no more than 30 days before the effective date. The notice must state the date, time and purpose for entering. In addition, the time scheduled for entry must be between 8 a.m. to 9 p.m., unless another time is agreed upon. It’s important to '
            ),
            {
              text: t('faq:know your rights'),
              href: 'https://liv.rent/blog/2018/09/your-rights-as-a-tenant/'
            },
            t('faq: as a tenant.')
          ]
        }
      ]
    },
    {
      title: t('faq:Does it cost to rent on liv.rent?'),
      path: 'does-it-cost-to-rent-livrent',
      answers: [
        {
          steps: [
            <Trans
              key="No, liv.rent is completely free for renters."
              i18nKey="faq:No, liv.rent is completely free for renters."
            >
              No, liv.rent is completely free for renters. By signing up, you
              can enjoy all of our{' '}
              <ItemLink href="https://liv.rent/renters">features</ItemLink> at
              no additional cost:
              <ul>
                <li>
                  <ItemLink
                    href="https://liv.rent/blog/2019/10/verification-process/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Verified landlords
                  </ItemLink>{' '}
                  to rent safer
                </li>
                <li>
                  <ItemLink
                    href="https://liv.rent/blog/2019/08/apply-to-multiple-listings/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Renter Resume
                  </ItemLink>{' '}
                  to apply to multiple listings with one account
                </li>
                <li>
                  <ItemLink
                    href="https://liv.rent/blog/2020/05/feature-update-view-rental-listings-online-with-video-tours/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Video Tours
                  </ItemLink>
                </li>
                <li>
                  <ItemLink
                    href="https://liv.rent/blog/2019/10/trust-score-explained/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Trust Score
                  </ItemLink>{' '}
                  to showcase your credibility as a renter
                </li>
                <li>
                  <ItemLink
                    href="https://liv.rent/blog/2020/04/secure-digital-rental-app-for-tenants-landlords/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Digital Contracts
                  </ItemLink>{' '}
                  to paperlessly secure your rental
                </li>
              </ul>
            </Trans>
          ]
        }
      ]
    },
    {
      title: t(
        'faq:What does a verified listing and verified landlord mean? And how do I know if a landlord or listing is verified?'
      ),
      path: 'what-does-verified-mean',
      answers: [
        {
          steps: [
            t(
              'faq:A verified listing and verified landlord on liv.rent means that our team have manually validated the information of a rental listing or an individual landlord from external sources (including but not limited to government-issued ID, land titles or official documents such as a business license). This is used to confirm the validity of the rental listing and the identity of the landlord.'
            )
          ]
        },
        {
          steps: [
            t(
              "faq:If a landlord or listing is verified, you will see a verified badge (checkmark icon) next to the address of the listing or name of the landlord. >> Learn more about liv.rent's "
            ),
            {
              text: t('faq:verification process'),
              href: 'https://liv.rent/blog/2019/10/verification-process/'
            }
          ]
        }
      ]
    },
    {
      title: t('faq:How do I apply to a rental listing?'),
      path: 'how-to-apply-to-listing',
      answers: [
        {
          steps: [
            <Trans
              key="In order to apply to a listing on liv.rent, you need to first"
              i18nKey="faq:In order to apply to a listing on liv.rent, you need to first"
            >
              In order to apply to a listing on liv.rent, you need to first{' '}
              <ItemLink
                href="https://liv.rent/renters"
                rel="noopener noreferrer"
                target="_blank"
              >
                sign up
              </ItemLink>{' '}
              and complete your{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/08/apply-to-multiple-listings/"
                rel="noopener noreferrer"
                target="_blank"
              >
                online renter resume
              </ItemLink>{' '}
              by providing basic information such as name, date of birth, email,
              phone number, and preferred language.
            </Trans>
          ]
        },
        {
          steps: [
            <Trans
              key="Similar to how landlords are verified, renters also go through a"
              i18nKey="faq:Similar to how landlords are verified, renters also go through a"
            >
              Similar to how landlords are verified, renters also go through a{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/10/verification-process/"
                rel="noopener noreferrer"
                target="_blank"
              >
                verification process
              </ItemLink>{' '}
              which contributes to the{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/10/trust-score-explained/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Trust Score
              </ItemLink>
              , our rating system used by landlords to evaluate your credibility
              as a renter. After completing your resume, you no longer have to
              worry about completing any additional forms. You can apply to
              multiple listings at once.
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('faq:Why is my Equifax score different from my Trust Score?'),
      path: 'why-is-credit-score-different-trust-score',
      answers: [
        {
          steps: [
            t('faq:Your '),
            {
              text: t('faq:Trust Score'),
              href: 'https://liv.rent/blog/2019/10/trust-score-explained/'
            },
            t(
              'faq: is not the same as your Equifax Score because Trust Score uses a powerful algorithm that corroborates information from multiple sources like your LinkedIn profile, references, and financial records to assess your credibility as a renter. It provides a more secure and accurate measure to determine your integrity.'
            )
          ]
        }
      ]
    },
    {
      title: t('faq:Why is my Trust Score so low?'),
      path: 'why-trust-score-low',
      answers: [
        {
          steps: [
            <Trans
              key="Your Trust Score may be low due to missing information in certain sections of your profile."
              i18nKey="faq:Your Trust Score may be low due to missing information in certain sections of your profile."
            >
              Your{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/10/trust-score-explained/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Trust Score
              </ItemLink>{' '}
              may be low due to missing information in certain sections of your
              profile.
            </Trans>
          ]
        },
        {
          steps: [
            <Trans
              key="To improve your Trust Score"
              i18nKey="faq:To improve your Trust Score"
            >
              To improve your{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/10/trust-score-explained/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Trust Score
              </ItemLink>
              <ul>
                <li>Complete your profile</li>
                <li>
                  Complete liv.rent&apos;s{' '}
                  <ItemLink
                    href="https://liv.rent/blog/2019/10/verification-process/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    verification process
                  </ItemLink>
                </li>
                <li>
                  Upload additional financial documents (e.g. pay stubs,
                  employment letter, tax slips, and more)
                </li>
              </ul>
            </Trans>
          ]
        },
        {
          steps: [
            <Trans
              key="Check out this blog post on how you can improve your Trust Score."
              i18nKey="faq:Check out this blog post on how you can improve your Trust Score."
            >
              Check out this blog post on how you can{' '}
              <ItemLink
                href="https://liv.rent/blog/2018/10/improving-your-trust-score/"
                rel="noopener noreferrer"
                target="_blank"
              >
                improve your Trust Score
              </ItemLink>
              .
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('faq:How do I pay rent online?'),
      path: 'how-to-pay-rent-online',
      answers: [
        {
          steps: [
            t(
              'faq:You can pay rent online through liv.rent, either in-app or on the web. There are several payment options you can choose from including Visa/Mastercard/American Express, UnionPay or bitcoin.'
            )
          ]
        },
        {
          steps: [
            <Trans
              key="Learn more about how you can pay rent through liv.rent to your landlord."
              i18nKey="faq:Learn more about how you can pay rent through liv.rent to your landlord."
            >
              <ItemLink
                href="https://liv.rent/blog/2020/01/how-to-pay-rent-to-your-landlord/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Learn more
              </ItemLink>{' '}
              about how you can pay rent through liv.rent to your landlord.
            </Trans>
          ]
        }
      ]
    },
    {
      title: t(
        'faq:Are there service fees associated with paying rent online through liv.rent?'
      ),
      path: 'service-fees-paying-rent',
      answers: [
        {
          steps: [
            <Trans
              key="If you’re paying by credit card — Visa, Mastercard, or American Express — then you will have to cover the"
              i18nKey="faq:If you’re paying by credit card — Visa, Mastercard, or American Express — then you will have to cover the"
            >
              If you’re paying by credit card — Visa, Mastercard, or American
              Express — then you will have to cover the{' '}
              <ItemLink
                href="https://stripe.com/en-ca/pricing"
                rel="noopener noreferrer"
                target="_blank"
              >
                standard processing fee
              </ItemLink>{' '}
              charged by Stripe, the platform used to facilitate these payments.
            </Trans>
          ]
        },
        {
          steps: [
            t(
              'faq:If you pay with UnionPay, you will be charged a standard processing fee of 2.5% per transaction.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:If you choose to use liv.rent’s Pay by Bitcoin feature, then you will pay a nominal, non-refundable fee of 0.5% per transaction. This is charged by liv.rent to cover the costs associated with facilitating these transactions. Additionally, you will have to cover any processing fees charged by the cryptocurrency network that you use.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:These fees will be labelled as “Processing fees” on your payment details page.'
            )
          ]
        }
      ]
    },
    {
      title: t('faq:Why do I have to pay processing fees on liv.rent?'),
      path: 'why-do-i-pay-processing-fees',
      answers: [
        {
          steps: [
            t(
              'faq:Most of these processing fees are charged by third parties like Stripe and UnionPay, which liv.rent has no control over. You will see the same fees being applied on other rent collection platforms.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:For liv.rent-specific fees, including the processing fee for bitcoin payments, we only charge when necessary. These fees are collected to help facilitate the transaction and to cover administrative costs.'
            )
          ]
        }
      ]
    },
    {
      title: t('faq:Why can’t I select a payment option?'),
      path: 'why-cant-i-select-payment-option',
      answers: [
        {
          steps: [
            t(
              'faq:To pay your rent through liv.rent, you will have to wait for your landlord to send a payment request, which will appear in your chat timeline. Once you receive the notification, click on the request and follow the payment flow. This can be done in-app or online.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:Now, you should be able to see a drop-down menu with three payment options: Visa/Mastercard/American Express, UnionPay, and Pay by Bitcoin.'
            )
          ]
        },
        {
          steps: [
            t('faq:Select your payment option and follow the instructions.')
          ]
        }
      ]
    },
    {
      title: t(
        'faq:The payment request says I’ve underpaid, what does that mean?'
      ),
      path: 'what-does-underpaid-mean',
      answers: [
        {
          steps: [
            t(
              'faq:This instance may occur if you pay with bitcoin since fluctuations in the exchange rate can affect your payment amount.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:In other words, the exchange rate, which is calculated at the time of payment, may result in a paid amount that is less than the requested amount. In this case, you would receive a message saying that you have underpaid. To resolve, all you need to do is pay the remaining balance.'
            )
          ]
        },
        {
          steps: [
            <Trans
              key="For more information on underpayment, you can refer to"
              i18nKey="faq:For more information on underpayment, you can refer to"
            >
              For more information on underpayment, you can refer to{' '}
              <ItemLink
                href="https://liv.rent/terms"
                rel="noopener noreferrer"
                target="_blank"
              >
                Section 4.10(f) of our Terms and Conditions
              </ItemLink>
              .
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('faq:If I overpaid, how can I request a refund?'),
      path: 'how-to-request-refund',
      answers: [
        {
          steps: [
            t(
              'faq:This instance may occur if you pay by bitcoin since fluctuations in the exchange rate can affect your payment amount.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:In other words, the exchange rate, which is calculated at the time of payment, may result in a paid amount that is greater than the requested amount.'
            )
          ]
        },
        {
          steps: [
            t(
              'faq:Since the entire payment will be converted into fiat currency and transferred to the landlord and liv.rent does not hold on to any overpaid funds, you will need to contact your landlord directly to arrange for a refund or credit based on an agreed method.'
            )
          ]
        },
        {
          steps: [
            <Trans
              key="For more information on overpayment, you can refer to"
              i18nKey="faq:For more information on overpayment, you can refer to"
            >
              For more information on overpayment, you can refer to{' '}
              <ItemLink
                href="https://liv.rent/terms"
                rel="noopener noreferrer"
                target="_blank"
              >
                Section 4.10(g) of our Terms and Conditions
              </ItemLink>
              .
            </Trans>
          ]
        }
      ]
    },
    {
      title: t(
        'faq:Can I get a refund if I made a mistake in my rent payment?'
      ),
      path: 'can-i-get-a-refund',
      answers: [
        {
          steps: [t('faq:Contact your landlord directly to make arrangements.')]
        }
      ]
    },
    {
      title: t(
        'faq:Under what circumstances do I contact liv.rent for a refund?'
      ),
      path: 'when-to-contact-livrent-refund',
      answers: [
        {
          steps: [
            <Trans
              key="For rare cases of invalid or cancelled payments, liv.rent may issue a refund. Please view"
              i18nKey="faq:For rare cases of invalid or cancelled payments, liv.rent may issue a refund. Please view"
            >
              For rare cases of invalid or cancelled payments, liv.rent may
              issue a refund. Please view{' '}
              <ItemLink
                href="https://liv.rent/terms"
                rel="noopener noreferrer"
                target="_blank"
              >
                Sections 4.10(h) and 4.10(d) of our Terms and Conditions
              </ItemLink>{' '}
              for the applicable refund situations.
            </Trans>
          ]
        },
        {
          steps: [
            <Trans
              key="If the situation applies, renters may submit a refund request to"
              i18nKey="faq:If the situation applies, renters may submit a refund request to"
            >
              If the situation applies, renters may submit a refund request to{' '}
              <ItemLink
                href="mailto:support@liv.rent"
                rel="noopener noreferrer"
                target="_blank"
              >
                support@liv.rent
              </ItemLink>
              . liv.rent will then review the request and issue a refund if
              deemed valid.
            </Trans>
          ]
        },
        {
          steps: [
            <Trans
              key="For information on how the refund amount will be calculated, refer to"
              i18nKey="faq:For information on how the refund amount will be calculated, refer to"
            >
              For information on how the refund amount will be calculated, refer
              to{' '}
              <ItemLink
                href="https://liv.rent/terms"
                rel="noopener noreferrer"
                target="_blank"
              >
                Section 4.10(j) of our Terms and Conditions
              </ItemLink>
              .
            </Trans>
          ]
        }
      ]
    },
    {
      title: t(
        'faq:What if I want to pay rent online through liv.rent, but my landlord is not on your platform?'
      ),
      path: 'landlord-isnt-on-livrent',
      answers: [
        {
          steps: [
            <Trans
              key="If your landlord is not on liv.rent, we can get them onboarded right away. Get them to contact us through our"
              i18nKey="faq:If your landlord is not on liv.rent, we can get them onboarded right away. Get them to contact us through our"
            >
              If your landlord is not on liv.rent, we can get them onboarded
              right away. Get them to contact us through our{' '}
              <ItemLink
                href="mailto:support@liv.rent"
                rel="noopener noreferrer"
                target="_blank"
              >
                support@liv.rent
              </ItemLink>{' '}
              email. We can help them set up a free account and provide a quick
              tour of all the different{' '}
              <ItemLink
                href="https://liv.rent/landlords"
                rel="noopener noreferrer"
                target="_blank"
              >
                landlord features
              </ItemLink>{' '}
              – including electronic rent collection.
            </Trans>
          ]
        }
      ]
    },
    {
      title: t('faq:What should I do if I think I got scammed by my landlord?'),
      path: 'what-to-do-when-scammed',
      answers: [
        {
          steps: [
            <Trans
              key="Scams and fraudulent activity are remarkably uncommon on the liv.rent platform thanks to our extensive"
              i18nKey="faq:Scams and fraudulent activity are remarkably uncommon on the liv.rent platform thanks to our extensive"
            >
              Scams and fraudulent activity are remarkably uncommon on the
              liv.rent platform thanks to our extensive{' '}
              <ItemLink
                href="https://liv.rent/blog/2019/10/verification-process/"
                rel="noopener noreferrer"
                target="_blank"
              >
                verification process
              </ItemLink>
              . For an added measure against rental scams, we verify both our
              listings and landlords. Listings are verified by official
              documents or directly through mail, whereas landlords are verified
              by government-issued ID or business license. So, during your
              search, make sure to look for the verified “checkmark” next to the
              listing and landlord profile.
            </Trans>
          ]
        },
        {
          steps: [
            <Trans
              key="However, if you believe you have been the victim of a scam or suspect fraudulent activity"
              i18nKey="faq:However, if you believe you have been the victim of a scam or suspect fraudulent activity"
            >
              However, if you believe you have been the victim of a scam or
              suspect fraudulent activity, then contact us immediately at{' '}
              <ItemLink
                href="mailto:support@liv.rent"
                rel="noopener noreferrer"
                target="_blank"
              >
                support@liv.rent
              </ItemLink>{' '}
              and we will begin an investigation.
            </Trans>
          ]
        },
        {
          steps: [
            t(
              'faq:Since all correspondence between you and your landlord including payment requests is stored in your chat timeline, you will have a record of everything which can be used as supporting evidence.'
            )
          ]
        }
      ]
    }
  ]

  return <FaqAccordion data={RENTER_TIPS} />
}

export default Renters
